import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const PrivacyContext = createContext(null);
const CONSENT_KEY = 'foundly_cookie_consent';
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_ID || 'G-YPGP1C44BN';

const readStoredConsent = () => {
    if (typeof window === 'undefined') return null;
    try {
        const stored = window.localStorage.getItem(CONSENT_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        console.warn('[Privacy] Unable to read consent from storage:', error);
        return null;
    }
};

export const PrivacyProvider = ({ children }) => {
    const [consent, setConsent] = useState(() => readStoredConsent());
    const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);
    const [isCookieBannerVisible, setCookieBannerVisible] = useState(!readStoredConsent());
    const [forcePreferencesOpen, setForcePreferencesOpen] = useState(false);
    const analyticsScriptLoadedRef = useRef(false);

    const persistConsent = useCallback((value) => {
        setConsent(value);
        try {
            window.localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
        } catch (error) {
            console.warn('[Privacy] Unable to save consent to storage:', error);
        }
    }, []);

    const acceptAllCookies = useCallback(() => {
        const newConsent = { analytics: true, timestamp: Date.now() };
        persistConsent(newConsent);
        setCookieBannerVisible(false);
    }, [persistConsent]);

    const rejectAllCookies = useCallback(() => {
        const newConsent = { analytics: false, timestamp: Date.now() };
        persistConsent(newConsent);
        setCookieBannerVisible(false);
    }, [persistConsent]);

    const setAnalyticsConsent = useCallback((enabled) => {
        const updatedConsent = { analytics: Boolean(enabled), timestamp: Date.now() };
        persistConsent(updatedConsent);
        setCookieBannerVisible(false);
    }, [persistConsent]);

    const openPrivacyModal = useCallback(() => setPrivacyModalOpen(true), []);
    const closePrivacyModal = useCallback(() => setPrivacyModalOpen(false), []);

    const openCookiePreferences = useCallback(() => {
        setForcePreferencesOpen(true);
        setCookieBannerVisible(true);
    }, []);

    const acknowledgePreferencesReminder = useCallback(() => {
        setForcePreferencesOpen(false);
    }, []);

    const loadAnalyticsScript = useCallback(() => {
        if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || analyticsScriptLoadedRef.current) {
            return;
        }

        const existingScript = document.getElementById('ga-script');
        if (existingScript) {
            analyticsScriptLoadedRef.current = true;
            return;
        }

        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        script.async = true;
        script.id = 'ga-script';
        script.onload = () => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){ window.dataLayer.push(arguments); }
            window.gtag = window.gtag || gtag;
            window.gtag('js', new Date());
            window.gtag('config', GA_MEASUREMENT_ID, {
                anonymize_ip: true,
                allow_google_signals: false,
                allow_ad_personalization_signals: false
            });
            analyticsScriptLoadedRef.current = true;
        };
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !GA_MEASUREMENT_ID) return;
        window[`ga-disable-${GA_MEASUREMENT_ID}`] = !(consent?.analytics);
        if (consent?.analytics) {
            loadAnalyticsScript();
        }
    }, [consent, loadAnalyticsScript]);

    useEffect(() => {
        if (!consent) {
            setCookieBannerVisible(true);
        }
    }, [consent]);

    const contextValue = useMemo(() => ({
        consent,
        isPrivacyModalOpen,
        isCookieBannerVisible,
        forcePreferencesOpen,
        acceptAllCookies,
        rejectAllCookies,
        setAnalyticsConsent,
        openPrivacyModal,
        closePrivacyModal,
        openCookiePreferences,
        acknowledgePreferencesReminder
    }), [
        consent,
        isPrivacyModalOpen,
        isCookieBannerVisible,
        forcePreferencesOpen,
        acceptAllCookies,
        rejectAllCookies,
        setAnalyticsConsent,
        openPrivacyModal,
        closePrivacyModal,
        openCookiePreferences,
        acknowledgePreferencesReminder
    ]);

    return (
        <PrivacyContext.Provider value={contextValue}>
            {children}
        </PrivacyContext.Provider>
    );
};

export const usePrivacy = () => {
    const context = useContext(PrivacyContext);
    if (!context) {
        throw new Error('usePrivacy must be used within a PrivacyProvider');
    }
    return context;
};

